import { useCallback, useEffect, useRef, useState } from 'react'
import { store } from '../../redux/store'

// No backend endpoint for "past AI searches" exists yet — persisted client-side
// only, same precedent as the "Always listen" preference in SabraahAiFloater.jsx.
const HISTORY_KEY = 'sabraah_ai_history'
const HISTORY_LIMIT = 30

function readHistory() {
  try {
    const raw = window.localStorage.getItem(HISTORY_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function writeHistory(list) {
  try {
    window.localStorage.setItem(HISTORY_KEY, JSON.stringify(list))
  } catch {
    /* ignore */
  }
}

function getUserAccessToken() {
  try {
    return store.getState()?.auth?.access_token || ''
  } catch {
    return ''
  }
}

function getCustomerProfile() {
  try {
    const user = store.getState()?.auth?.user || {}
    const email = String(user.email || '').trim()
    const phone = String(user.phone || user.mobile || '').replace(/\D/g, '').slice(-10)
    const name = [user.first_name, user.last_name].filter(Boolean).join(' ').trim()
      || String(user.name || '').trim()
    return {
      ...(name ? { customer_name: name } : {}),
      ...(phone.length === 10 ? { customer_phone: phone } : {}),
      ...(email.includes('@') ? { customer_email: email } : {}),
    }
  } catch {
    return {}
  }
}

function resolveAiCandidates() {
  const raw = (process.env.REACT_APP_SABRAH_AI_URL || '').trim().replace(/\/$/, '')
  const direct = 'http://127.0.0.1:8000'
  const proxy = '/sabrah-ai'
  const bases = []
  const add = (base) => {
    if (base && !bases.includes(base)) bases.push(base)
  }
  // CRA's setupProxy only loads at npm start. If /sabrah-ai 404s, fall back to :8000.
  if (process.env.NODE_ENV === 'development') add(direct)
  add(raw)
  add(proxy)
  add(direct)
  return bases
}

const AI_CANDIDATES = resolveAiCandidates()

function isUsableAiResponse(res) {
  if (!res || !res.ok) return false
  const type = res.headers.get('content-type') || ''
  return !type.includes('text/html')
}

async function fetchAi(path, options, preferredBase) {
  const ordered = [
    preferredBase,
    ...AI_CANDIDATES.filter((base) => base !== preferredBase),
  ].filter(Boolean)
  let lastResponse = null
  let lastError = null
  for (const base of ordered) {
    try {
      const res = await fetch(`${base}${path}`, options)
      if (isUsableAiResponse(res)) return { res, base }
      lastResponse = res
    } catch (err) {
      lastError = err
    }
  }
  if (lastResponse) return { res: lastResponse, base: ordered[0] }
  throw lastError || new Error('Could not start a Sabraah conversation.')
}

function networkErrorMessage(err) {
  const msg = String(err?.message || '')
  if (!msg || msg === 'Failed to fetch' || err?.name === 'TypeError') {
    return 'Cannot reach Sabraah AI. Start sabrah-ai on port 8000 and sabrah-travel-backend on port 8001.'
  }
  return msg
}

const SpeechRecognition =
  typeof window !== 'undefined'
    ? window.SpeechRecognition || window.webkitSpeechRecognition || null
    : null

const WAKE_NAME = String.raw`(?:e?s[aeiou]{0,2}br+[aeoh]+|sahara|sarah|sara|saber+|sabre|sabah|subaru|supra|zebra)`

const WAKE_PATTERNS = [
  new RegExp(String.raw`\b(?:hey|hi|hello|ok(?:ay)?|oye|yo)\s+${WAKE_NAME}\b`, 'i'),
  new RegExp(String.raw`\bhe+\s+${WAKE_NAME}\b`, 'i'),
  new RegExp(String.raw`\b${WAKE_NAME}\b`, 'i'),
]

function normalizeHeard(text) {
  return String(text || '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

export function isWakePhrase(text) {
  const cleaned = normalizeHeard(text)
  if (!cleaned) return false
  if (WAKE_PATTERNS.some((re) => re.test(cleaned))) return true
  const compact = cleaned.replace(/\s+/g, '')
  return new RegExp(
    String.raw`(?:hey|hi|hello|okay|ok|oye|yo)?e?${WAKE_NAME}`,
    'i',
  ).test(compact)
}

export function stripWakePhrase(text) {
  let cleaned = normalizeHeard(text)
  if (!cleaned) return ''
  cleaned = cleaned.replace(/^(?:hey|hi|hello|ok(?:ay)?|oye|yo)\s+/i, '')
  cleaned = cleaned.replace(new RegExp(`^${WAKE_NAME}\\b`, 'i'), '')
  return cleaned.trim()
}

const MAX_RECORD_MS = 9000
const SILENCE_MS = 1100
const SPEECH_LEVEL = 0.01
const NO_SPEECH_MS = 4000
const MIN_SPEECH_MS = 280

function playWakeChime() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)()
    const now = ctx.currentTime
    ;[523.25, 659.25].forEach((freq, i) => {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.type = 'sine'
      osc.frequency.value = freq
      gain.gain.setValueAtTime(0.0001, now)
      gain.gain.exponentialRampToValueAtTime(0.08, now + 0.02 + i * 0.05)
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.28 + i * 0.08)
      osc.connect(gain)
      gain.connect(ctx.destination)
      osc.start(now + i * 0.05)
      osc.stop(now + 0.35 + i * 0.08)
    })
    setTimeout(() => ctx.close().catch(() => {}), 600)
  } catch {
    /* optional */
  }
}

function toAppUrl(path) {
  if (!path) return ''
  if (/^https?:\/\//i.test(path)) return path
  const prefix = path.startsWith('/') ? path : `/${path}`
  if (typeof window === 'undefined') return prefix
  return `${window.location.origin}${prefix}`
}

function extractError(payload) {
  if (!payload) return 'Something went wrong.'
  if (typeof payload.detail === 'string') return payload.detail
  if (payload.detail && payload.detail.message) return payload.detail.message
  if (Array.isArray(payload.detail) && payload.detail[0]?.msg) return payload.detail[0].msg
  if (payload.message) return payload.message
  return 'Something went wrong.'
}

function extractErrorCode(payload) {
  if (!payload) return ''
  if (payload.detail && typeof payload.detail === 'object') {
    return String(payload.detail.code || '')
  }
  return String(payload.code || '')
}

function isSessionGone(payload, message) {
  const code = extractErrorCode(payload)
  const text = String(message || '').toLowerCase()
  return (
    code === 'session_not_found'
    || text.includes('session expired')
    || text.includes('session not found')
  )
}

export default function useSabraahVoice({ alwaysListen, onWakeOpen, onAlwaysListenDenied } = {}) {
  const [status, setStatus] = useState('Ready')
  const [mode, setMode] = useState('idle')
  const [messages, setMessages] = useState([])
  const [history, setHistory] = useState(readHistory)
  const [offerings, setOfferings] = useState(null)
  const [bookingUrl, setBookingUrl] = useState('')
  const [error, setError] = useState('')
  const [connected, setConnected] = useState(null)
  const [muted, setMuted] = useState(false)

  const sessionIdRef = useRef(null)
  const aiBaseRef = useRef(AI_CANDIDATES[0])
  const busyRef = useRef(false)
  const commandRef = useRef(false)
  const alwaysRef = useRef(Boolean(alwaysListen))
  const mutedRef = useRef(false)
  const recorderRef = useRef(null)
  const streamRef = useRef(null)
  const chunksRef = useRef([])
  const wakeRef = useRef(null)
  const wakeStartingRef = useRef(false)
  const wakeLockRef = useRef(false)
  const audioRef = useRef(null)
  const autoListenRef = useRef(false)
  const pendingFollowUpRef = useRef(false)
  const silenceRef = useRef({})
  const onWakeOpenRef = useRef(onWakeOpen)
  const onAlwaysListenDeniedRef = useRef(onAlwaysListenDenied)
  const onWakeDetectedRef = useRef(null)
  const startListeningRef = useRef(null)
  const stopListeningRef = useRef(null)
  const sendTextRef = useRef(null)

  alwaysRef.current = Boolean(alwaysListen)
  mutedRef.current = muted
  onWakeOpenRef.current = onWakeOpen
  onAlwaysListenDeniedRef.current = onAlwaysListenDenied

  const idleLabel = useCallback(
    () => (alwaysRef.current ? 'Say Sabraah' : 'Ready'),
    [],
  )
  const idleMode = useCallback(
    () => (alwaysRef.current ? 'waiting' : 'idle'),
    [],
  )

  const restoreIdle = useCallback(() => {
    if (!busyRef.current && !commandRef.current) {
      setStatus(idleLabel())
      setMode(idleMode())
    }
  }, [idleLabel, idleMode])

  const addBubble = useCallback((role, text) => {
    if (!text) return
    setMessages((prev) => [...prev, { id: `${role}-${Date.now()}-${prev.length}`, role, text }])
  }, [])

  // One entry per recognized user utterance (text typed or voice transcribed
  // server-side) — newest first, capped so localStorage doesn't grow unbounded.
  const recordHistory = useCallback((text) => {
    const trimmed = String(text || '').trim()
    if (!trimmed) return
    setHistory((prev) => {
      const next = [{ id: `h-${Date.now()}`, text: trimmed, timestamp: Date.now() }, ...prev].slice(0, HISTORY_LIMIT)
      writeHistory(next)
      return next
    })
  }, [])

  const clearHistory = useCallback(() => {
    setHistory([])
    writeHistory([])
  }, [])

  const stopPlayback = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.src = ''
      audioRef.current = null
    }
  }, [])

  const stopSilenceWatch = useCallback(() => {
    const s = silenceRef.current
    if (s.silenceTimer) clearTimeout(s.silenceTimer)
    if (s.maxRecordTimer) clearTimeout(s.maxRecordTimer)
    if (s.noSpeechTimer) clearTimeout(s.noSpeechTimer)
    if (s.analyserRaf) cancelAnimationFrame(s.analyserRaf)
    if (s.analyserCtx) s.analyserCtx.close().catch(() => {})
    silenceRef.current = {}
  }, [])

  const stopWakeListening = useCallback(() => {
    const recognition = wakeRef.current
    if (!recognition) return
    try {
      recognition.onend = null
      recognition.onerror = null
      recognition.onresult = null
      recognition.stop()
    } catch {
      /* already stopped */
    }
    wakeRef.current = null
  }, [])

  const playAudioBase64 = useCallback((b64, mime = 'audio/mpeg') => {
    if (mutedRef.current || !b64) {
      restoreIdle()
      return Promise.resolve()
    }
    stopPlayback()
    return new Promise((resolve) => {
      const audio = new Audio(`data:${mime};base64,${b64}`)
      audioRef.current = audio
      setStatus('Speaking')
      setMode('speaking')
      audio.onended = () => {
        if (audioRef.current === audio) audioRef.current = null
        restoreIdle()
        resolve()
      }
      audio.onerror = () => {
        setError("Could not play Sabraah's voice.")
        restoreIdle()
        resolve()
      }
      audio.play().catch(() => {
        setError('Browser blocked audio. Click the orb and try again.')
        restoreIdle()
        resolve()
      })
    })
  }, [restoreIdle, stopPlayback])

  const ensureSession = useCallback(async () => {
    if (sessionIdRef.current) return sessionIdRef.current
    const token = getUserAccessToken()
    const profile = getCustomerProfile()
    const { res, base } = await fetchAi('/api/v1/sessions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        source: 'Voice UI',
        ...profile,
        ...(token ? { user_access_token: token } : {}),
      }),
    }, aiBaseRef.current)
    aiBaseRef.current = base
    if (!res.ok) throw new Error('Could not start a Sabraah conversation.')
    const data = await res.json()
    sessionIdRef.current = data.session_id
    return sessionIdRef.current
  }, [])

  const postChatText = useCallback(async (message) => {
    await ensureSession()
    const token = getUserAccessToken()
    const profile = getCustomerProfile()
    const { res, base } = await fetchAi('/api/v1/chat/text', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        session_id: sessionIdRef.current,
        message,
        ...profile,
        ...(token ? { user_access_token: token } : {}),
      }),
    }, aiBaseRef.current)
    aiBaseRef.current = base
    return res
  }, [ensureSession])

  const postChatVoice = useCallback(async (blob) => {
    await ensureSession()
    const form = new FormData()
    form.append('session_id', sessionIdRef.current)
    form.append('audio', blob, 'speech.webm')
    const token = getUserAccessToken()
    if (token) form.append('user_access_token', token)
    const profile = getCustomerProfile()
    Object.entries(profile).forEach(([key, value]) => {
      if (value) form.append(key, value)
    })
    const { res, base } = await fetchAi('/api/v1/chat/voice', { method: 'POST', body: form }, aiBaseRef.current)
    aiBaseRef.current = base
    return res
  }, [ensureSession])

  const startWakeListening = useCallback(() => {
    if (!alwaysRef.current || busyRef.current || commandRef.current || wakeStartingRef.current || !SpeechRecognition) {
      return
    }
    if (wakeRef.current) return

    wakeStartingRef.current = true
    const recognition = new SpeechRecognition()
    recognition.continuous = true
    recognition.interimResults = true
    recognition.lang = 'en-IN'
    recognition.maxAlternatives = 5

    recognition.onresult = (event) => {
      if (!alwaysRef.current || busyRef.current || commandRef.current) return
      const recent = []
      const start = Math.max(0, event.results.length - 8)
      for (let i = start; i < event.results.length; i += 1) {
        const result = event.results[i]
        const alts = Array.from(result).map((alt) => alt.transcript || '')
        recent.push(alts[0])
        const blob = `${recent.join(' ')} ${alts.join(' ')}`
        const matched = alts.find((text) => isWakePhrase(text)) || (isWakePhrase(blob) ? blob : '')
        if (matched) {
          onWakeDetectedRef.current?.(matched)
          return
        }
      }
    }

    recognition.onerror = (event) => {
      if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
        setError('Microphone permission needed for always listen. Click the Sabraah icon, allow mic, then turn Always listen on.')
        onAlwaysListenDeniedRef.current?.()
        restoreIdle()
        return
      }
      // no-speech / aborted / network — let onend restart
    }

    recognition.onend = () => {
      wakeRef.current = null
      wakeStartingRef.current = false
      if (alwaysRef.current && !busyRef.current && !commandRef.current) {
        setTimeout(() => startWakeListening(), 250)
      }
    }

    try {
      recognition.start()
      wakeRef.current = recognition
      restoreIdle()
    } catch {
      wakeRef.current = null
      setTimeout(() => startWakeListening(), 400)
    } finally {
      wakeStartingRef.current = false
    }
  }, [restoreIdle])

  const primeMicrophone = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      startWakeListening()
      return false
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      stream.getTracks().forEach((track) => track.stop())
      startWakeListening()
      return true
    } catch {
      setError('Microphone permission needed for always listen. Allow mic, then turn it back on.')
      onAlwaysListenDeniedRef.current?.()
      return false
    }
  }, [startWakeListening])

  const handleChatResponse = useCallback(async (res, preloadedPayload) => {
    const payload = preloadedPayload !== undefined
      ? preloadedPayload
      : await res.json().catch(() => null)
    if (!res.ok) {
      const err = new Error(extractError(payload))
      err.payload = payload
      err.status = res.status
      throw err
    }
    if (!payload || payload.ignored) return
    addBubble('user', payload.user_text)
    recordHistory(payload.user_text)
    addBubble('assistant', payload.assistant_text)
    const isMainMenu = /what would you like to do/i.test(payload.assistant_text || '')
    const nextOfferings = !isMainMenu && payload.offerings && typeof payload.offerings === 'object'
      ? Object.fromEntries(
        Object.entries(payload.offerings).filter(([, value]) => (
          Array.isArray(value) ? value.length > 0 : Boolean(value)
        )),
      )
      : null
    setOfferings(nextOfferings && Object.keys(nextOfferings).length ? nextOfferings : null)
    setBookingUrl(payload.booking_url || '')
    if (payload.tts_error) setError(payload.tts_error)
    await playAudioBase64(payload.audio_base64, payload.audio_mime_type || 'audio/mpeg')

    if (payload.open_booking && payload.booking_url) {
      const bookingHref = toAppUrl(payload.booking_url)
      window.setTimeout(() => {
        window.open(bookingHref, '_blank', 'noopener,noreferrer')
      }, 400)
    } else if (payload.payment_url) {
      autoListenRef.current = false
      window.setTimeout(() => {
        window.location.href = payload.payment_url
      }, 400)
      return
    }

    if (autoListenRef.current) {
      autoListenRef.current = false
      pendingFollowUpRef.current = true
      setTimeout(() => {
        pendingFollowUpRef.current = false
        if (!busyRef.current && !commandRef.current) startListeningRef.current?.({ followUp: true })
      }, 350)
    }
  }, [addBubble, playAudioBase64, recordHistory])

  const sendText = useCallback(async (message) => {
    if (!message || busyRef.current) return
    busyRef.current = true
    stopWakeListening()
    setError('')
    stopPlayback()
    setStatus('Thinking')
    setMode('thinking')
    try {
      let res = await postChatText(message)
      if (!res.ok) {
        const payload = await res.clone().json().catch(() => null)
        if (isSessionGone(payload, extractError(payload))) {
          sessionIdRef.current = null
          res = await postChatText(message)
        }
      }
      await handleChatResponse(res)
      setError('')
    } catch (err) {
      setError(networkErrorMessage(err))
      autoListenRef.current = false
    } finally {
      busyRef.current = false
      if (alwaysRef.current && !commandRef.current && !autoListenRef.current && !pendingFollowUpRef.current) {
        startWakeListening()
      } else {
        restoreIdle()
      }
    }
  }, [handleChatResponse, postChatText, restoreIdle, startWakeListening, stopPlayback, stopWakeListening])

  const sendVoice = useCallback(async (blob, { followUp = false } = {}) => {
    busyRef.current = true
    setError('')
    setStatus('Thinking')
    setMode('thinking')
    try {
      let res = await postChatVoice(blob)
      let payload = await res.json().catch(() => null)
      if (!res.ok && isSessionGone(payload, extractError(payload))) {
        sessionIdRef.current = null
        res = await postChatVoice(blob)
        payload = await res.json().catch(() => null)
      }
      if (payload?.ignored) {
        setError('')
        autoListenRef.current = false
        return
      }
      autoListenRef.current = alwaysRef.current
      await handleChatResponse(res, payload)
      setError('')
    } catch (err) {
      autoListenRef.current = false
      if (followUp) setError('')
      else setError(networkErrorMessage(err))
    } finally {
      busyRef.current = false
      if (alwaysRef.current && !commandRef.current && !autoListenRef.current && !pendingFollowUpRef.current) {
        startWakeListening()
      } else {
        restoreIdle()
      }
    }
  }, [handleChatResponse, postChatVoice, restoreIdle, startWakeListening])

  const startSilenceWatch = useCallback((stream, onSilence, onNoSpeech, { ignoreMs = 0 } = {}) => {
    stopSilenceWatch()
    const state = { heardSpeech: false, speechStartedAt: 0 }
    const ignoreUntil = Date.now() + Math.max(0, ignoreMs)
    state.maxRecordTimer = setTimeout(() => onSilence(), MAX_RECORD_MS)
    state.noSpeechTimer = setTimeout(() => {
      if (!state.heardSpeech) onNoSpeech()
    }, NO_SPEECH_MS + Math.max(0, ignoreMs))

    try {
      const analyserCtx = new (window.AudioContext || window.webkitAudioContext)()
      const source = analyserCtx.createMediaStreamSource(stream)
      const analyser = analyserCtx.createAnalyser()
      analyser.fftSize = 1024
      source.connect(analyser)
      const data = new Uint8Array(analyser.fftSize)
      state.analyserCtx = analyserCtx

      const tick = () => {
        if (!commandRef.current) return
        analyser.getByteTimeDomainData(data)
        let sum = 0
        for (let i = 0; i < data.length; i += 1) {
          const v = (data[i] - 128) / 128
          sum += v * v
        }
        const rms = Math.sqrt(sum / data.length)
        if (Date.now() < ignoreUntil) {
          state.analyserRaf = requestAnimationFrame(tick)
          return
        }
        if (rms > SPEECH_LEVEL) {
          if (!state.heardSpeech) {
            state.heardSpeech = true
            state.speechStartedAt = Date.now()
            if (state.noSpeechTimer) {
              clearTimeout(state.noSpeechTimer)
              state.noSpeechTimer = null
            }
          }
          if (state.silenceTimer) {
            clearTimeout(state.silenceTimer)
            state.silenceTimer = null
          }
        } else if (state.heardSpeech && !state.silenceTimer) {
          const spokenFor = Date.now() - state.speechStartedAt
          if (spokenFor >= MIN_SPEECH_MS) {
            state.silenceTimer = setTimeout(() => onSilence(), SILENCE_MS)
          }
        }
        state.analyserRaf = requestAnimationFrame(tick)
      }
      state.analyserRaf = requestAnimationFrame(tick)
    } catch {
      /* max duration fallback */
    }
    silenceRef.current = state
  }, [stopSilenceWatch])

  const startListening = useCallback(async ({ followUp = false, ignoreMs = 0 } = {}) => {
    if (busyRef.current || commandRef.current) return
    setError('')
    stopPlayback()
    stopWakeListening()

    let mediaStream
    try {
      mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch (err) {
      const denied = err && (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError')
      setError(
        denied
          ? 'Microphone permission denied. Allow mic access and try again.'
          : 'Could not access the microphone on this device.',
      )
      if (alwaysRef.current) startWakeListening()
      else restoreIdle()
      return
    }

    chunksRef.current = []
    commandRef.current = true
    streamRef.current = mediaStream
    const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
      ? 'audio/webm;codecs=opus'
      : 'audio/webm'
    const mediaRecorder = new MediaRecorder(mediaStream, { mimeType: mime })
    recorderRef.current = mediaRecorder
    mediaRecorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) chunksRef.current.push(event.data)
    }
    mediaRecorder.onstop = async () => {
      const discarded = mediaRecorder._discard
      stopSilenceWatch()
      commandRef.current = false
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop())
        streamRef.current = null
      }
      const blob = new Blob(chunksRef.current, { type: mime })
      chunksRef.current = []
      recorderRef.current = null

      if (discarded || !blob.size || blob.size < 2500) {
        if (!followUp) setError('No speech detected. Please try again.')
        restoreIdle()
        if (alwaysRef.current) startWakeListening()
        return
      }
      setStatus('Thinking')
      setMode('thinking')
      await sendVoice(blob, { followUp })
    }

    mediaRecorder.start(200)
    startSilenceWatch(mediaStream, () => {
      if (commandRef.current) stopListeningRef.current?.()
    }, () => {
      if (commandRef.current) stopListeningRef.current?.({ discard: true })
    }, { ignoreMs })
    setStatus('Listening')
    setMode('listening')
  }, [restoreIdle, sendVoice, startSilenceWatch, startWakeListening, stopPlayback, stopSilenceWatch, stopWakeListening])

  const stopListening = useCallback(({ discard = false } = {}) => {
    stopSilenceWatch()
    const mediaRecorder = recorderRef.current
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder._discard = discard
      setStatus(discard ? idleLabel() : 'Thinking')
      setMode(discard ? idleMode() : 'thinking')
      mediaRecorder.stop()
    }
  }, [idleLabel, idleMode, stopSilenceWatch])

  startListeningRef.current = startListening
  stopListeningRef.current = stopListening
  sendTextRef.current = sendText

    onWakeDetectedRef.current = async () => {
    if (busyRef.current || commandRef.current || wakeLockRef.current) return
    wakeLockRef.current = true
    stopWakeListening()
    setError('')
    onWakeOpenRef.current?.()
    playWakeChime()
    // Chrome wake STT is too inaccurate for commands (e.g. "thank you" → "peace").
    // Open the panel, greet, then capture the real command with Whisper.
    autoListenRef.current = true
    try {
      await sendTextRef.current('Hey Sabraah')
    } finally {
      window.setTimeout(() => {
        wakeLockRef.current = false
      }, 800)
    }
  }

  useEffect(() => {
    let cancelled = false
    fetchAi('/health', undefined, aiBaseRef.current)
      .then(async ({ res, base }) => {
        if (cancelled) return
        aiBaseRef.current = base
        if (!res.ok) throw new Error('health failed')
        const data = await res.json()
        if (data.travel_backend === 'reachable') {
          setConnected(true)
        } else {
          setConnected(false)
          setError('Travel API is unreachable. Start api-repository on port 8002.')
        }
      })
      .catch(() => {
        if (cancelled) return
        setConnected(false)
        setError('Cannot reach Sabraah AI. Start sabrah-ai on port 8000.')
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!alwaysListen) {
      stopWakeListening()
      restoreIdle()
      return undefined
    }
    startWakeListening()
    const arm = () => {
      primeMicrophone()
    }
    window.addEventListener('pointerdown', arm, { once: true })
    return () => window.removeEventListener('pointerdown', arm)
  }, [alwaysListen, primeMicrophone, restoreIdle, startWakeListening, stopWakeListening])

  useEffect(() => () => {
    stopWakeListening()
    stopPlayback()
    stopSilenceWatch()
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop())
    }
  }, [stopPlayback, stopSilenceWatch, stopWakeListening])

  const resetConversation = useCallback(async () => {
    stopPlayback()
    stopListening({ discard: true })
    setMessages([])
    setOfferings(null)
    setBookingUrl('')
    setError('')
    if (sessionIdRef.current) {
      try {
        await fetchAi(`/api/v1/sessions/${sessionIdRef.current}/reset`, { method: 'POST' }, aiBaseRef.current)
      } catch {
        /* ignore */
      }
    }
    sessionIdRef.current = null
    restoreIdle()
    if (alwaysRef.current) startWakeListening()
  }, [restoreIdle, startWakeListening, stopListening, stopPlayback])

  return {
    status,
    mode,
    messages,
    history,
    clearHistory,
    offerings,
    bookingUrl,
    error,
    connected,
    muted,
    supportsWake: Boolean(SpeechRecognition),
    startListening,
    stopListening,
    sendText,
    resetConversation,
    setMuted,
    primeMicrophone,
    listening: mode === 'listening',
    speaking: mode === 'speaking',
    thinking: mode === 'thinking',
  }
}
